import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Minus, Plus, Trash2, Loader2, CreditCard, Wallet, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { useCart, cartKey } from "@/context/CartContext";
import { fetchSlots, fetchAvailableDates, createOrder, lookupCustomerByPhone } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function serializeErrorDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d)))).join("; ");
  }
  if (typeof detail === "object") return JSON.stringify(detail);
  return String(detail);
}

const PACK_SIZES = { spam_musubi_2: 2, lumpia_6: 6 };

function formatDateLabel(iso) {
  const d = new Date(iso + "T12:00:00");
  const today = new Date();
  today.setHours(0,0,0,0);
  const isToday = iso === today.toISOString().slice(0, 10);
  const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return isToday ? `Today · ${label}` : label;
}

export default function CartSheet() {
  const { items, setQty, removeItem, totals, isOpen, setIsOpen, clear } = useCart();
  const [slots, setSlots] = useState([]);
  const [dates, setDates] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", pickup_date: "", pickup_slot: "", payment_method: "card", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [recognized, setRecognized] = useState(false);
  const lookupTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setRecognized(false);
      return;
    }
    lookupTimer.current = setTimeout(async () => {
      try {
        const data = await lookupCustomerByPhone(digits);
        if (!data?.name) return;
        setForm(f => ({
          ...f,
          name: f.name.trim() ? f.name : (data.name || f.name),
        }));
        setRecognized(true);
        toast.success(`Welcome back${data.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
      } catch (err) {
        // Best-effort lookup — silence expected network noise but surface real bugs in dev tools
        if (process.env.NODE_ENV !== "production") console.debug("customer lookup failed", err);
      }
    }, 500);
    return () => { if (lookupTimer.current) clearTimeout(lookupTimer.current); };
  }, [form.phone]);

  useEffect(() => {
    fetchAvailableDates().then(setDates).catch(() => setDates([]));
  }, []);

  useEffect(() => {
    if (!form.pickup_date && dates.length) {
      setForm(f => ({ ...f, pickup_date: dates[0] }));
    }
  }, [dates, form.pickup_date]);

  useEffect(() => {
    if (!form.pickup_date) { setSlots([]); return; }
    fetchSlots(form.pickup_date).then(s => {
      setSlots(s);
      // reset pickup_slot if it's not in new slot list
      setForm(f => (s.includes(f.pickup_slot) ? f : { ...f, pickup_slot: "" }));
    }).catch(() => setSlots([]));
  }, [form.pickup_date]);

  const canCheckout = items.length > 0 && form.name.trim() && form.phone.trim() && form.pickup_date && form.pickup_slot;

  const handleCheckout = async () => {
    if (!canCheckout) {
      toast.error("Please fill in name, phone, pickup date and time");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim(),
        customer_email: form.email.trim() || null,
        pickup_date: form.pickup_date,
        pickup_slot: form.pickup_slot,
        payment_method: form.payment_method,
        items: items.map(i => ({ id: i.id, quantity: i.quantity, variant: i.variant || null })),
        notes: form.notes,
        origin_url: window.location.origin,
      };
      const res = await createOrder(payload);

      if (res.payment_method === "card" && res.checkout_url) {
        // Redirect to Stripe hosted checkout. Show a fallback link in case
        // the browser blocks the programmatic navigation (some in-app browsers do).
        setRedirect({ url: res.checkout_url });
        toast.success("Redirecting to secure payment…");
        // Small timeout so the toast/state paints; then navigate.
        setTimeout(() => {
          window.location.assign(res.checkout_url);
        }, 150);
        return;
      }

      // Pay at pickup
      clear();
      setIsOpen(false);
      navigate(`/order/${res.order_id}?confirmed=1`);
    } catch (e) {
      toast.error(serializeErrorDetail(e?.response?.data?.detail, e?.message || "Failed to place order"));
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => { if (!submitting) setIsOpen(o); }}>
      <SheetContent
        side="right"
        data-testid="cart-sheet"
        className="w-full sm:max-w-lg bg-[#F9F6F0] border-l border-[#E5D9C5] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-extrabold text-[#2C241B]">Your Order</SheetTitle>
        </SheetHeader>

        {redirect && (
          <div className="mt-4 bg-white rounded-2xl border border-[#E5D9C5] p-4 slide-up" data-testid="redirect-panel">
            <div className="flex items-center gap-2 text-[#2C5E3B] font-semibold">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending you to secure Stripe checkout…
            </div>
            <p className="mt-2 text-sm text-[#7A6C5D]">If nothing happens automatically, tap the button below.</p>
            <a
              data-testid="manual-checkout-link"
              href={redirect.url}
              rel="noopener noreferrer"
              className="cta-primary mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-sm"
            >
              Open Stripe Checkout <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {items.length === 0 ? (
          <div className="mt-10 text-center" data-testid="cart-empty-state">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#F5EEE0] flex items-center justify-center">
              <Wallet className="w-7 h-7 text-[#7A6C5D]" />
            </div>
            <p className="mt-4 text-[#7A6C5D]">Your cart is empty.</p>
            <button
              onClick={() => setIsOpen(false)}
              className="mt-4 text-[#D9381E] font-semibold text-sm"
              data-testid="cart-continue-browsing-btn"
            >
              Browse the menu →
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {items.map(it => {
              const key = cartKey(it.id, it.variant);
              const pack = PACK_SIZES[it.id];
              const totalPieces = pack ? pack * it.quantity : null;
              return (
                <div key={key} data-testid={`cart-item-${key}`} className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-[#E5D9C5]">
                  <div className="flex-1">
                    <div className="font-display font-bold text-[#2C241B]">
                      {it.name}
                      {it.variant && <span className="text-[#7A6C5D] font-medium"> · {it.variant}</span>}
                    </div>
                    <div className="text-sm text-[#7A6C5D]">
                      ${it.price.toFixed(2)} {pack ? "per pack" : "each"}
                      {pack && (
                        <span className="ml-1.5 text-[11px] font-semibold text-[#2C5E3B]">
                          · {it.quantity} pack{it.quantity > 1 ? "s" : ""} = {totalPieces} pieces
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-[#F5EEE0] rounded-full px-2 py-1">
                    <button data-testid={`cart-qty-decrease-${key}`} onClick={() => setQty(it.id, it.variant, it.quantity - 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center border border-[#E5D9C5]">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span data-testid={`cart-qty-${key}`} className="min-w-[24px] text-center font-semibold text-sm">{it.quantity}</span>
                    <button data-testid={`cart-qty-increase-${key}`} onClick={() => setQty(it.id, it.variant, it.quantity + 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center border border-[#E5D9C5]">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button data-testid={`cart-remove-${key}`} onClick={() => removeItem(it.id, it.variant)} className="p-2 text-[#7A6C5D] hover:text-[#D9381E]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}

            <Separator className="my-4 bg-[#E5D9C5]" />

            <div className="space-y-4">
              <h4 className="font-display font-extrabold text-lg text-[#2C241B]">Pickup Details</h4>

              <div>
                <Label htmlFor="cust-name" className="text-sm font-semibold text-[#2C241B]">Name</Label>
                <Input id="cust-name" data-testid="cart-input-name" placeholder="Your full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#E5D9C5] rounded-xl" />
              </div>

              <div>
                <Label htmlFor="cust-phone" className="text-sm font-semibold text-[#2C241B] flex items-center gap-1.5">
                  Phone
                  {recognized && (
                    <span data-testid="repeat-guest-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#2C5E3B] bg-[#2C5E3B]/10 px-2 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3" /> Welcome back
                    </span>
                  )}
                </Label>
                <Input id="cust-phone" data-testid="cart-input-phone" placeholder="(555) 123-4567" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white border-[#E5D9C5] rounded-xl" />
              </div>

              <div>
                <Label htmlFor="cust-email" className="text-sm font-semibold text-[#2C241B]">Email <span className="text-[#7A6C5D] font-normal">(optional — get a pickup-day reminder)</span></Label>
                <Input id="cust-email" data-testid="cart-input-email" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#E5D9C5] rounded-xl" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-[#2C241B]">Pickup Date</Label>
                  <Select value={form.pickup_date} onValueChange={v => setForm({ ...form, pickup_date: v })}>
                    <SelectTrigger data-testid="cart-select-date" className="mt-1 bg-white border-[#E5D9C5] rounded-xl">
                      <SelectValue placeholder="Select date" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {dates.map(iso => (
                        <SelectItem key={iso} value={iso} data-testid={`date-option-${iso}`}>{formatDateLabel(iso)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#2C241B]">Pickup Time</Label>
                  <Select value={form.pickup_slot} onValueChange={v => setForm({ ...form, pickup_slot: v })}>
                    <SelectTrigger data-testid="cart-select-slot" className="mt-1 bg-white border-[#E5D9C5] rounded-xl">
                      <SelectValue placeholder="Select slot" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {slots.map(s => (
                        <SelectItem key={s} value={s} data-testid={`slot-option-${s.replace(/[\s:]/g, '-')}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm font-semibold text-[#2C241B]">Notes (optional)</Label>
                <Input id="notes" data-testid="cart-input-notes" placeholder="Anything the kitchen should know?" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white border-[#E5D9C5] rounded-xl" />
              </div>

              <div>
                <Label className="text-sm font-semibold text-[#2C241B]">Payment</Label>
                <div data-testid="pay-card-option" className="mt-2 flex items-center gap-2 p-3 rounded-xl border-2 border-[#D9381E] bg-[#FDECE8]">
                  <CreditCard className="w-4 h-4 text-[#D9381E]" />
                  <span className="font-semibold text-sm text-[#2C241B]">Card only — pay online securely at checkout</span>
                </div>
              </div>
            </div>

            <Separator className="my-4 bg-[#E5D9C5]" />

            <div className="bg-white rounded-2xl p-4 border border-[#E5D9C5]">
              <div className="flex justify-between text-sm text-[#7A6C5D]">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mt-3 items-baseline">
                <span className="font-display font-extrabold text-lg text-[#2C241B]">Total</span>
                <span data-testid="cart-total" className="font-display font-extrabold text-2xl text-[#D9381E]">${totals.total.toFixed(2)}</span>
              </div>
            </div>

            <button
              data-testid="cart-checkout-btn"
              disabled={!canCheckout || submitting}
              onClick={handleCheckout}
              className="cta-primary w-full rounded-full py-3.5 font-display font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  Pay & Place Order
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-[11px] text-[#7A6C5D] text-center pb-6">
              Pickup address is sent by email after you place your order.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
